import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  ApplicantState,
  Applicant,
  FetchPaginatedApplicantsPayload,
  ApplicantData,
} from "@/modules/applicants/types";
import {
  FetchPayload,
  FetchAuditPayload,
} from "@/modules/applicants/types/index";
import { BaseApplicant } from "@/modules/applicants/vuex/state";

import API from "@/axios";

const actions: ActionTree<ApplicantState, RootState> = {
  fetchApplicants({ commit, state }, payload: FetchPayload) {
    const { status } = payload;
    const { sortBy, sortOrder, isFetchingApplicants } = state;
    if (!isFetchingApplicants) commit("setIsFetchingApplicants", true);

    const url = `/applicants?PageSize=20&status=${status}&sortBy=${sortBy}&sortOrder=${sortOrder}&search=${state.query}`;
    return API.get(url).then((response) => {
      commit("setIsFetchingApplicants", false);
      if (status === "all") {
        commit("setApplicants", response.data);
      } else if (status === "approved") {
        commit("setAllApprovedApplicants", response.data);
      } else if (status === "rejected") {
        commit("setAllRejectedApplicants", response.data);
      } else if (status === "new") {
        commit("setAllPendingApplicants", response.data);
      }
    });
  },
  fetchPaginatedApplicants(
    { commit, state },
    payload: FetchPaginatedApplicantsPayload
  ) {
    const { sortBy, sortOrder, query, isFetchingApplicants } = state;
    if (!isFetchingApplicants) commit("setIsFetchingApplicants", true);

    const { pageIndex, status } = payload;
    return API.get(
      `/applicants?PageSize=20&page=${pageIndex}&status=${status}&sortBy=${sortBy}&sortOrder=${sortOrder}&search=${query}`
    ).then((response) => {
      commit("setIsFetchingApplicants", false);
      if (response.data) {
        commit("setPageIndex", response.data.pageIndex);
        commit("setHasNextPage", response.data.hasNextPage);
        if (status === "all") {
          commit("setApplicants", response.data);
        } else if (status === "approved") {
          commit("setAllApprovedApplicants", response.data);
        } else if (status === "rejected") {
          commit("setAllRejectedApplicants", response.data);
        } else if (status === "new") {
          commit("setAllPendingApplicants", response.data);
        }
      }
    });
  },
  fetchPaginatedApplicantAudits({ commit, state }, payload: FetchAuditPayload) {
    const { isFetchingApplicantAudits } = state;
    if (!isFetchingApplicantAudits)
      commit("setIsFetchingApplicantAudits", true);

    const { pageIndex, entityId } = payload;
    return API.get(
      `/auditlogs/logs?PageSize=10&page=${pageIndex}&entity=IdDocumentData&entityId=${entityId}`
    ).then((response) => {
      commit("setIsFetchingApplicantAudits", false);
      if (response.data) {
        commit("setPageIndex", response.data.pageIndex);
        commit("setHasNextPage", response.data.hasNextPage);
        commit("setApplicantAudits", response.data);
      }
    });
  },
  downloadApplicants({ commit, state }, payload: FetchPayload) {
    const getLocalTimeZoneOffset = function () {
      const offset = new Date().getTimezoneOffset();
      return offset / 60;
    };

    const { status } = payload;

    const url = `/applicants/download?&status=${status}&search=${
      state.query
    }&timeZoneOffset=${getLocalTimeZoneOffset()}`;
    return API.get(url).then((response) => {
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8",
      });
      const file = window.URL.createObjectURL(blob);
      const docUrl = document.createElement("a");
      docUrl.href = file;
      const now = new Date();
      const formatted = `${now.getMonth()}-${now.getDay()}-${now.getFullYear()}_${now.getHours()}_${now.getMinutes()}_${now.getSeconds()}`;
      docUrl.setAttribute("download", `applicants_${status}_${formatted}.csv`);
      document.body.appendChild(docUrl);
      docUrl.click();
      document.body.removeChild(docUrl);
    });
  },
  setQuery({ commit }, query: string) {
    commit("setQuery", query);
  },
  setApplicantListOrder({ commit }, applicants: Array<Applicant>) {
    commit("setApplicantListOrder", applicants);
  },
  setApprovedApplicantListOrder({ commit }, applicants: Array<Applicant>) {
    commit("setApprovedApplicantListOrder", applicants);
  },
  fetchCurrentApplicant({ commit }, applicantId) {
    //Clear previous applicant
    commit("setIsLoadingCurrentApplicant", true);
    commit("setCurrentApplicant", BaseApplicant);
    return API.get(`/applicants/${applicantId}`).then((response) => {
      commit("setCurrentApplicant", response.data);
      commit("setIsLoadingCurrentApplicant", false);
    });
  },
  acceptApplicant({ dispatch }, applicantId) {
    // TODO find a way to type the dispatch calls, potential for failure/typos
    return API.post(`/applicants/${applicantId}/approve`).then(async () => {
      await dispatch("fetchApplicants", { fetchAll: true });
    });
  },
  updateApplicant({ dispatch, commit }, payload: ApplicantData) {
    const {
      applicantId,
      firstName,
      lastName,
      sex,
      dob,
      addressOne,
      addressTwo,
      dateIssued,
      nationality,
      middleName,
      email,
      expirationDate,
    } = payload;

    return API.put(`/applicants/${applicantId}`, {
      firstName,
      lastName,
      middleName,
      email,
      sex,
      dob,
      addressOne,
      addressTwo,
      dateIssued,
      nationality,
      expirationDate,
    })
      .then(async (response) => {
        commit("setCurrentApplicant", response.data);
        commit("setIsLoadingCurrentApplicant", false);
        await dispatch("fetchApplicants", { fetchAll: true });
      })
      .catch((error) => {
        // Handle error if needed
        
        throw error; // Re-throwing the error to propagate it to the caller
      });
  },
  rejectApplicant({ dispatch }, applicantId) {
    return API.post(`/applicants/${applicantId}/deny`).then(async () => {
      await dispatch("fetchApplicants", { fetchAll: true });
    });
  },
  deleteApplicant({ dispatch }, applicantId) {
    return API.delete(`/applicants/${applicantId}`).then(async () => {
      await dispatch("fetchApplicants", { fetchAll: true });
    });
  },
  resetApplicant({ dispatch }, applicantId) {
    return API.post(`/applicants/${applicantId}/reset`).then(async () => {
      await dispatch("fetchApplicants", { fetchAll: true });
    });
  },
  viewAllPending({ commit, dispatch }) {
    commit("setIsViewing", "pending");
    dispatch("fetchApplicants", { status: "new" });
  },
  viewAllApplicants({ commit, dispatch }) {
    commit("setIsViewing", "all");
    dispatch("fetchApplicants", { status: "all" });
  },
  viewAllApproved({ commit, dispatch }) {
    commit("setIsViewing", "approved");
    dispatch("fetchApplicants", { status: "approved" });
  },
  viewAllRejected({ commit, dispatch }) {
    commit("setIsViewing", "rejected");
    dispatch("fetchApplicants", { status: "rejected" });
  },
  setSortOrder({ commit }, sortOrder) {
    commit("setSortOrder", sortOrder);
  },
  setSortBy({ commit }, sortBy) {
    commit("setSortBy", sortBy);
  },
};

export default actions;
